import { jsx, jsxs } from "react/jsx-runtime";
import { useRef, Fragment } from "react";
import assert from "assert";
import { Field } from "../private/Field/Field.mjs";
import { useDateRangePicker } from "@react-aria/datepicker";
import { useDateRangePickerState } from "@react-stately/datepicker";
import { CalendarRange } from "./Calendar.mjs";
import { Box } from "../Box/Box.mjs";
import { getLocalTimeZone } from "@internationalized/date";
import { useField } from "@react-aria/label";
import { Input } from "../DateField/Input.mjs";
import { dateInput } from "../DateField/DateField.css.mjs";
import { ActionField } from "../DateField/ActionField.mjs";
import "../Text/TextContext.mjs";
import "../Heading/HeadingContext.mjs";
import "../../css/lineHeightContainer.css.mjs";
import "../../css/reset/reset.css.mjs";
import "../../css/atoms/sprinkles.css.mjs";
import "../../hooks/useIcon/icon.css.mjs";
import "../../css/typography.css.mjs";
import { IconCalendar } from "../icons/IconCalendar/IconCalendar.mjs";
import { Text } from "../Text/Text.mjs";
import { useDefaultMessages } from "../useDefaultMessages/useDefaultMessages.mjs";
import { dateToCalendarDate } from "../DateField/DateField.mjs";
const getLabelProps = (props) => {
  if ("label" in props) return { label: props.label };
  if ("aria-label" in props) return { label: props["aria-label"] };
  if ("aria-labelledby" in props) return { labelId: props["aria-labelledby"] };
  return {};
};
function DateRangeField(props) {
  var _a, _b, _c;
  const localTimeZone = getLocalTimeZone();
  const containerRef = useRef(null);
  assert(
    !((props == null ? void 0 : props.minDate) && (props == null ? void 0 : props.maxDate)) || props.minDate <= props.maxDate,
    "maxDate must be after minDate"
  );
  const value = props.value ? {
    start: dateToCalendarDate(props.value[0]),
    end: dateToCalendarDate(props.value[1])
  } : props.value;
  const formattedProps = {
    isDisabled: props.disabled,
    onFocus: props.onFocus,
    onBlur: props.onBlur,
    isReadOnly: props.readOnly,
    id: props.id,
    minValue: props.minDate ? dateToCalendarDate(props.minDate) : void 0,
    maxValue: props.maxDate ? dateToCalendarDate(props.maxDate) : void 0,
    isDateUnavailable: props.shouldDisableDate ? (date) => props.shouldDisableDate(date.toDate(localTimeZone)) : void 0,
    value,
    onChange: (range) => {
      if (!range) {
        props.onChange(null);
      } else {
        props.onChange([range.start.toDate(localTimeZone), range.end.toDate(localTimeZone)]);
      }
    },
    shouldForceLeadingZeros: true,
    ...getLabelProps(props)
  };
  const dateRangePickerState = useDateRangePickerState(formattedProps);
  const ref = useRef(null);
  const { groupProps, startFieldProps, endFieldProps, buttonProps, calendarProps, dialogProps } = useDateRangePicker(formattedProps, dateRangePickerState, ref);
  const { defaultMessages } = useDefaultMessages();
  const field = useField({
    ...props
  });
  const seperatorOpactiy = ((_a = dateRangePickerState.value) == null ? void 0 : _a.start) || ((_b = dateRangePickerState.value) == null ? void 0 : _b.end) ? 1 : 0;
  return /* @__PURE__ */ jsx(
    Field,
    {
      ...props,
      ...field,
      value: (_c = props == null ? void 0 : props.value) == null ? void 0 : _c.toString(),
      labelId: void 0,
      secondaryIcon: /* @__PURE__ */ jsx(
        ActionField,
        {
          id: buttonProps.id,
          icon: /* @__PURE__ */ jsx(IconCalendar, {}),
          label: defaultMessages.DateField.actionFieldLabel,
          onAction: (e) => {
            if (buttonProps == null ? void 0 : buttonProps.onPress) buttonProps.onPress(e);
          },
          "aria-describedby": buttonProps["aria-describedby"],
          "aria-expanded": buttonProps["aria-expanded"],
          "aria-label": buttonProps["aria-label"],
          "aria-labelledby": buttonProps["aria-labelledby"],
          disabled: buttonProps.isDisabled
        }
      ),
      alwaysShowSecondaryIcon: true,
      children: (overlays, fieldProps, icon, secondaryIcon, prefix) => /* @__PURE__ */ jsxs(Fragment, { children: [
        icon,
        prefix,
        /* @__PURE__ */ jsx(
          Box,
          {
            ...fieldProps,
            disabled: props.disabled,
            padding: "xxsmall",
            width: "full",
            ref: containerRef,
            children: /* @__PURE__ */ jsxs(
              Box,
              {
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                gap: "xsmall",
                ...groupProps,
                ref,
                children: [
                  /* @__PURE__ */ jsx(Input, { fieldProps: startFieldProps }),
                  /* @__PURE__ */ jsx(Box, { className: dateInput, style: { opacity: seperatorOpactiy }, children: /* @__PURE__ */ jsx(Text, { tone: props.disabled ? "disabled" : void 0, children: "-" }) }),
                  /* @__PURE__ */ jsx(Input, { fieldProps: endFieldProps })
                ]
              }
            )
          }
        ),
        overlays,
        secondaryIcon,
        dateRangePickerState.isOpen && /* @__PURE__ */ jsx(Box, { ...dialogProps, children: /* @__PURE__ */ jsx(
          CalendarRange,
          {
            inputRef: containerRef,
            calendarProps,
            onClose: dateRangePickerState.close
          }
        ) })
      ] })
    }
  );
}
export {
  DateRangeField
};
